import {
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  isSignInWithEmailLink,
  RecaptchaVerifier,
  sendPasswordResetEmail,
  sendSignInLinkToEmail,
  signInWithCredential,
  signInWithEmailAndPassword,
  signInWithEmailLink,
  signInWithPopup,
  signInWithCustomToken,
  UserCredential,
} from 'firebase/auth';
import { auth } from '../auth/firebaseAuth';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';
import { hideLoading, showAuthMessage, showLoading } from '../redux/actions';
import { AuthProvider, verifyBeforeUpdateEmail } from '@firebase/auth/dist/node-esm';
import { AuthCredential, reauthenticateWithCredential, sendEmailVerification } from '@firebase/auth';
import { FirebaseError } from '@firebase/util';
import { message as antMessage, message } from 'antd';
import { env } from '../configs/EnvironmentConfig';
import { sentenceCase } from 'sentence-case';
import { userApi } from '../api';
import { idTokenResultKey, profileQueryKey } from '../api/query';
import { MultiFactorVerification } from '@quick/client';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useEffect } from 'react';

interface EmailAndPassword {
  email: string;
  password: string;
}

interface ResolveMultiFactorRequest {
  verificationId: string;
  verificationCode: string;
}

interface VerifyMultiFactorRequest {
  phoneNumber: string;
}

export type AuthResult = AuthSuccess | AuthError;

export interface AuthSuccess {
  type: 'SUCCESS';
  token: string;
}

export interface AuthError {
  type: 'ERROR';
  message: string;
}

export const useAuthMutation = () => {
  const dispatch = useDispatch();
  const commonMutationOptions = {
    onMutate() {
      dispatch(showLoading());
    },
    onSettled() {
      dispatch(hideLoading());
    },
    onSuccess(result: any | undefined) {
      if (result && result.type === 'ERROR') {
        dispatch(showAuthMessage(result.message));
      }
    },
  };
  const useSignUpWithEmailAndPassword = useMutation(async (request: EmailAndPassword): Promise<AuthResult> => {
    try {
      const credential = await createUserWithEmailAndPassword(auth, request.email, request.password);
      return success(credential);
    } catch (e) {
      if (e.code === 'auth/email-already-in-use') {
        return error('Email is already in use. Please sign in instead.');
      } else {
        return error('Something went wrong. Please try again later');
      }
    }
  }, commonMutationOptions);
  const useSignInWithPopup = useMutation(async (provider: AuthProvider): Promise<AuthResult> => {
    try {
      const credential = await signInWithPopup(auth, provider);
      return success(credential);
    } catch (e) {
      return error(e);
    }
  }, commonMutationOptions);
  const useSignInWithCredential = useMutation(async (authCredential: AuthCredential): Promise<AuthResult> => {
    try {
      const credential = await signInWithCredential(auth, authCredential);
      return success(credential);
    } catch (e) {
      return error(e);
    }
  }, commonMutationOptions);
  const useSignInWithEmailAndPassword = useMutation(async (request: EmailAndPassword): Promise<AuthResult> => {
    try {
      const credential = await signInWithEmailAndPassword(auth, request.email, request.password);
      return success(credential);
    } catch (e) {
      return error(e);
    }
  }, commonMutationOptions);
  const useReauthenticateWithCredential = useMutation(async (password: string): Promise<AuthResult> => {
    try {
      const user = auth.currentUser!;
      const credential = await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email!, password));
      return await success(credential);
    } catch (e) {
      return error(e);
    }
  }, commonMutationOptions);
  const useSendPasswordResetEmail = useMutation(async (email: string) => {
    try {
      await sendPasswordResetEmail(auth, email, getActionCodeSettings(email));
      antMessage.success('Check you email to reset password!');
    } catch (e) {
      dispatch(showAuthMessage(errorMessage(e, 'Failed to send password reset email')));
    }
  }, commonMutationOptions);
  const useSendEmailVerification = useMutation(async () => {
    try {
      const user = auth.currentUser;
      if (!user || !user.email) {
        return;
      }
      await sendEmailVerification(user, getActionCodeSettings(user.email));
      message.success('Check your email for verification link');
    } catch (e) {
      dispatch(showAuthMessage(errorMessage(e, 'Failed to send verification link')));
    }
  }, commonMutationOptions);
  const useVerifyBeforeUpdateEmail = useMutation(async (email: string) => {
    try {
      const user = auth.currentUser;
      if (!user) {
        return;
      }
      await verifyBeforeUpdateEmail(user, email, getActionCodeSettings(email));
      message.success('Check your email for verification link');
    } catch (e) {
      dispatch(showAuthMessage(errorMessage(e, 'Failed to send verification link')));
    }
  }, commonMutationOptions);
  const useSendSignInEmailLink = useMutation(async (email: string) => {
    try {
      await sendSignInLinkToEmail(auth, email, getActionCodeSettings(email));
      message.success('Check your email to login!');
    } catch (e) {
      dispatch(showAuthMessage(errorMessage(e, 'Failed to send magic link')));
    }
  }, commonMutationOptions);
  const useSignInWithEmailLink = useMutation(async (emailQueryParam: string | null): Promise<AuthResult | null> => {
    if (!isSignInWithEmailLink(auth, window.location.href)) {
      return null;
    }
    // Additional state parameters can also be passed via URL.
    // This can be used to continue the user's intended action before triggering
    // the sign-in operation.
    // Get the email if available. This should be available if the user completes
    // the flow on the same device where they started it.
    let email = emailQueryParam;
    if (!email) {
      // User opened the link on a different device. To prevent session fixation
      // attacks, ask the user to provide the associated email again. For example:
      email = window.prompt('Please provide your email for confirmation');
    }
    try {
      // The client SDK will parse the code from the link for you.
      const credential = await signInWithEmailLink(auth, email!, window.location.href);
      return success(credential);
    } catch (e) {
      return error(e);
    }
  }, commonMutationOptions);
  return {
    useSignUpWithEmailAndPassword,
    useSignInWithEmailAndPassword,
    useSignInWithPopup,
    useSignInWithCredential,
    useSendEmailVerification,
    useVerifyBeforeUpdateEmail,
    useReauthenticateWithCredential,
    useSendPasswordResetEmail,
    useSendSignInEmailLink,
    useSignInWithEmailLink,
  };
};

export const useIdTokenResultQuery = () => {
  const [user] = useAuthState(auth);
  return useQuery(
    idTokenResultKey(),
    async () => {
      if (!user) {
        return undefined;
      }
      return await user.getIdTokenResult();
    },
    {
      staleTime: 0,
    }
  );
};

export const useMultiFactorId = () => {
  const idTokenResult = useIdTokenResultQuery().data;
  if (!idTokenResult) {
    return undefined;
  }
  return parseInt(idTokenResult.claims['multiFactorId'] as string);
};

export const useMultiFactorMutation = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const useVerifyMultiFactor = useMutation(
    async ({ phoneNumber }: VerifyMultiFactorRequest): Promise<MultiFactorVerification> => {
      try {
        const verifier: RecaptchaVerifier = window.recaptchaVerifier;
        await verifier.verify();
        return await userApi.startMultiFactorVerification({
          startMultiFactorVerification: {
            phoneNumber,
          },
        });
      } catch (e) {
        dispatch(showAuthMessage('Failed to send verification code'));
        throw e;
      }
    },
    {
      onSuccess() {
        queryClient.invalidateQueries(profileQueryKey());
      },
    }
  );
  const useResolveMultiFactor = useMutation(
    async ({ verificationId, verificationCode }: ResolveMultiFactorRequest) => {
      try {
        const multiFactorSession = await userApi.resolveMultiFactorVerification({
          resolveMultiFactorVerification: {
            verificationId,
            verificationCode,
          },
        });
        const userCredential = await signInWithCustomToken(auth, multiFactorSession.customToken);
        const tokenResult = await userCredential.user.getIdTokenResult();
        await queryClient.setQueryData(idTokenResultKey(), tokenResult);
      } catch (e) {
        dispatch(showAuthMessage('Failed to verify phone number'));
      }
    },
    {
      onSuccess() {
        queryClient.invalidateQueries(profileQueryKey());
      },
    }
  );
  return {
    useVerifyMultiFactor,
    useResolveMultiFactor,
  };
};

const success = async (credential: UserCredential): Promise<AuthSuccess> => {
  return {
    type: 'SUCCESS',
    token: await credential.user.getIdToken(),
  };
};

const error = (error: Error | string): AuthError => {
  if (typeof error === 'string') {
    return {
      type: 'ERROR',
      message: error,
    };
  }
  return {
    type: 'ERROR',
    message: errorMessage(error),
  };
};

export const EMAIL_QUERY_PARAM = 'email';

const getActionCodeSettings = (email: string) => ({
  url: `${env.REACT_APP_WEB_URL}/auth/login?${EMAIL_QUERY_PARAM}=${email}`,
  // This must be true.
  handleCodeInApp: true,
  // TODO uncomment after configuring dynamic link.
  // iOS: {
  //   bundleId: env.REACT_APP_IOS_BUNDLE_ID,
  // },
  // android: {
  //   packageName: env.REACT_APP_ANDROID_PACKAGE_NAME,
  //   installApp: true,
  // },
});

const errorMessage = (e: Error, defaultMessage?: string) => {
  if (e.name === 'FirebaseError') {
    const firebaseError = e as FirebaseError;
    if (firebaseError.code.startsWith('auth/')) {
      return sentenceCase(firebaseError.code.substring('auth/'.length));
    }
  }
  return defaultMessage ?? e.message;
};
